import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  FaCheckCircle,
  FaEnvelope,
  FaExclamationTriangle,
  FaIdCard,
  FaKey,
  FaLock,
  FaPhone,
  FaSave,
  FaShieldAlt,
  FaTimes,
  FaUserEdit
} from 'react-icons/fa';
import userService from '../services/userService';
import { normalizeRole } from '../utils/rbac';
import './EditUserDialog.css';

const EXIT_ANIMATION_MS = 220;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const getInitialState = (user, canManageRole, canManageStatus) => ({
  username: user?.username || '',
  full_name: user?.full_name || '',
  email: user?.email || '',
  phone: user?.phone || '',
  role: normalizeRole(user?.role || 'technician'),
  is_active: canManageStatus ? Boolean(user?.is_active) : true,
  wantsPasswordReset: false,
  password: '',
  passwordConfirm: ''
});

const getComparableSnapshot = (form, canManageRole, canManageStatus) => ({
  username: form.username.trim(),
  full_name: form.full_name.trim(),
  email: form.email.trim().toLowerCase(),
  phone: form.phone.trim(),
  role: canManageRole ? normalizeRole(form.role) : null,
  is_active: canManageStatus ? Boolean(form.is_active) : null,
  wantsPasswordReset: Boolean(form.wantsPasswordReset),
  password: form.wantsPasswordReset ? form.password : ''
});

const getRoleLabel = (roleOptions, role) => {
  const normalized = normalizeRole(role);
  return roleOptions.find((option) => option.value === normalized)?.label || normalized;
};

const validateForm = (form, { canManageRole, canManageStatus }) => {
  const errors = {};

  if (!form.username.trim()) {
    errors.username = 'اسم المستخدم مطلوب';
  }

  if (!form.full_name.trim()) {
    errors.full_name = 'الاسم الكامل مطلوب';
  }

  const normalizedEmail = form.email.trim().toLowerCase();
  if (!normalizedEmail) {
    errors.email = 'البريد الإلكتروني مطلوب';
  } else if (!EMAIL_REGEX.test(normalizedEmail)) {
    errors.email = 'صيغة البريد الإلكتروني غير صحيحة';
  }

  if (form.phone.trim() && form.phone.trim().length < 7) {
    errors.phone = 'رقم الهاتف غير مكتمل';
  }

  if (canManageRole && !normalizeRole(form.role)) {
    errors.role = 'اختر صلاحية صحيحة';
  }

  if (canManageStatus && typeof form.is_active !== 'boolean') {
    errors.is_active = 'حالة الحساب غير صالحة';
  }

  if (form.wantsPasswordReset) {
    if (form.password.length < 6) {
      errors.password = 'كلمة المرور الجديدة يجب أن تكون 6 أحرف على الأقل';
    }
    if (form.password !== form.passwordConfirm) {
      errors.passwordConfirm = 'تأكيد كلمة المرور غير مطابق';
    }
  }

  return errors;
};

const EditUserDialog = ({
  open,
  user,
  roleOptions,
  canManageRole,
  canManageStatus,
  onClose,
  onSuccess
}) => {
  const titleId = useId();
  const formId = useId();
  const mountedRef = useRef(true);
  const closeTimerRef = useRef(null);
  const firstInputRef = useRef(null);

  const [activeUser, setActiveUser] = useState(null);
  const [shouldRender, setShouldRender] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [form, setForm] = useState(getInitialState(null, canManageRole, canManageStatus));
  const [initialSnapshot, setInitialSnapshot] = useState(
    getComparableSnapshot(getInitialState(null, canManageRole, canManageStatus), canManageRole, canManageStatus)
  );
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (closeTimerRef.current) {
        window.clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (open && user) {
      const nextForm = getInitialState(user, canManageRole, canManageStatus);

      if (closeTimerRef.current) {
        window.clearTimeout(closeTimerRef.current);
      }

      setActiveUser(user);
      setShouldRender(true);
      setFieldErrors({});
      setSubmitError('');
      setIsSubmitting(false);
      setForm(nextForm);
      setInitialSnapshot(getComparableSnapshot(nextForm, canManageRole, canManageStatus));

      const frameId = window.requestAnimationFrame(() => {
        setIsVisible(true);
      });

      return () => {
        window.cancelAnimationFrame(frameId);
      };
    }

    if (!open && shouldRender) {
      setIsVisible(false);
      closeTimerRef.current = window.setTimeout(() => {
        if (!mountedRef.current) return;
        setShouldRender(false);
        setActiveUser(null);
      }, EXIT_ANIMATION_MS);
    }

    return undefined;
  }, [open, user, canManageRole, canManageStatus, shouldRender]);

  useEffect(() => {
    if (!shouldRender) return undefined;

    document.body.classList.add('user-edit-dialog-open');

    return () => {
      document.body.classList.remove('user-edit-dialog-open');
    };
  }, [shouldRender]);

  useEffect(() => {
    if (!open || !shouldRender) return undefined;

    const focusTimer = window.setTimeout(() => {
      firstInputRef.current?.focus();
      firstInputRef.current?.select();
    }, 40);

    const handleEscape = (event) => {
      if (event.key === 'Escape' && !isSubmitting) {
        onClose?.();
      }
    };

    document.addEventListener('keydown', handleEscape);

    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open, shouldRender, isSubmitting, onClose]);

  if (!shouldRender || !activeUser || typeof document === 'undefined') {
    return null;
  }

  const currentSnapshot = getComparableSnapshot(form, canManageRole, canManageStatus);
  const hasChanges = JSON.stringify(currentSnapshot) !== JSON.stringify(initialSnapshot);
  const validationErrors = validateForm(form, { canManageRole, canManageStatus });
  const isValid = Object.keys(validationErrors).length === 0;

  const handleFieldChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const nextErrors = { ...prev };
      delete nextErrors[field];
      return nextErrors;
    });
    setSubmitError('');
  };

  const handlePasswordToggle = (checked) => {
    setForm((prev) => ({
      ...prev,
      wantsPasswordReset: checked,
      password: checked ? prev.password : '',
      passwordConfirm: checked ? prev.passwordConfirm : ''
    }));
    setFieldErrors((prev) => {
      const nextErrors = { ...prev };
      delete nextErrors.password;
      delete nextErrors.passwordConfirm;
      return nextErrors;
    });
    setSubmitError('');
  };

  const handleBackdropClick = () => {
    if (!isSubmitting) {
      onClose?.();
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const nextErrors = validateForm(form, { canManageRole, canManageStatus });
    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      return;
    }

    if (!hasChanges) {
      return;
    }

    setFieldErrors({});
    setSubmitError('');
    setIsSubmitting(true);

    try {
      const payload = {
        username: form.username.trim(),
        full_name: form.full_name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim() || null
      };

      if (canManageRole) {
        payload.role = normalizeRole(form.role);
      }

      if (canManageStatus) {
        payload.is_active = Boolean(form.is_active);
      }

      if (form.wantsPasswordReset) {
        payload.password = form.password;
      }

      const response = await userService.update(activeUser.id, payload);

      if (!mountedRef.current) return;

      await Promise.resolve(onSuccess?.(response.user, response));
    } catch (error) {
      if (!mountedRef.current) return;
      setSubmitError(error.response?.data?.error || 'تعذر حفظ التعديلات. حاول مرة أخرى.');
      setIsSubmitting(false);
      return;
    }

    if (mountedRef.current) {
      setIsSubmitting(false);
    }
  };

  return createPortal(
    <div className={`user-edit-dialog-shell ${isVisible ? 'is-visible' : ''}`} dir="rtl">
      <button
        type="button"
        className="user-edit-dialog-backdrop"
        aria-label="إغلاق نافذة تعديل المستخدم"
        onClick={handleBackdropClick}
      />

      <section
        className="user-edit-dialog-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <header className="user-edit-dialog-header">
          <div className="user-edit-dialog-heading">
            <span className="user-edit-dialog-heading-icon">
              <FaUserEdit />
            </span>
            <div>
              <h2 id={titleId}>تعديل بيانات المستخدم</h2>
              <p>واجهة مستقرة وسريعة لتحديث بيانات الحساب بدون تجميد الصفحة.</p>
            </div>
          </div>

          <button
            type="button"
            className="user-edit-dialog-close"
            onClick={handleBackdropClick}
            disabled={isSubmitting}
            aria-label="إغلاق"
          >
            <FaTimes />
          </button>
        </header>

        <div className="user-edit-dialog-content">
          <aside className="user-edit-dialog-sidebar">
            <div className="user-edit-identity-card">
              <div className="user-edit-avatar" aria-hidden="true">
                {(activeUser.full_name || activeUser.username || '?').trim().charAt(0).toUpperCase()}
              </div>

              <div className="user-edit-identity-copy">
                <strong>{activeUser.full_name || 'مستخدم بدون اسم'}</strong>
                <span>@{activeUser.username}</span>
              </div>

              <div className="user-edit-badges">
                <span className="user-edit-badge is-role">
                  <FaShieldAlt />
                  {getRoleLabel(roleOptions, activeUser.role)}
                </span>
                <span className={`user-edit-badge ${activeUser.is_active ? 'is-active' : 'is-inactive'}`}>
                  <FaCheckCircle />
                  {activeUser.is_active ? 'حساب نشط' : 'حساب غير نشط'}
                </span>
              </div>

              <dl className="user-edit-meta-list">
                <div>
                  <dt><FaIdCard /> اسم المستخدم</dt>
                  <dd>{activeUser.username || 'غير متوفر'}</dd>
                </div>
                <div>
                  <dt><FaEnvelope /> البريد الإلكتروني</dt>
                  <dd>{activeUser.email || 'غير متوفر'}</dd>
                </div>
                <div>
                  <dt><FaPhone /> الهاتف</dt>
                  <dd>{activeUser.phone || 'غير متوفر'}</dd>
                </div>
              </dl>
            </div>

            <div className="user-edit-guidance-card">
              <h3>ملاحظات الحفظ</h3>
              <ul>
                <li>يتم تحديث القائمة مباشرة بعد الحفظ بدون إعادة تحميل الصفحة.</li>
                <li>كلمة المرور اختيارية ولن تتغير إلا إذا فعّلت إعادة التعيين.</li>
                <li>إغلاق النافذة أثناء الحفظ معطل لمنع الحالات المعلقة أو الخلفيات العالقة.</li>
              </ul>
            </div>
          </aside>

          <form id={formId} className="user-edit-dialog-form" onSubmit={handleSubmit}>
            {submitError && (
              <div className="user-edit-message is-error" role="alert">
                <FaExclamationTriangle />
                <span>{submitError}</span>
              </div>
            )}

            <section className="user-edit-section">
              <div className="user-edit-section-header">
                <h3>البيانات الأساسية</h3>
                <p>حدّث البيانات الرئيسية للمستخدم مع تحقق فوري من المدخلات.</p>
              </div>

              <div className="user-edit-grid">
                <label className={`user-edit-field ${fieldErrors.username ? 'has-error' : ''}`}>
                  <span>اسم المستخدم</span>
                  <input
                    ref={firstInputRef}
                    type="text"
                    value={form.username}
                    onChange={(event) => handleFieldChange('username', event.target.value)}
                    disabled={isSubmitting}
                    autoComplete="off"
                  />
                  {fieldErrors.username && <small>{fieldErrors.username}</small>}
                </label>

                <label className={`user-edit-field ${fieldErrors.full_name ? 'has-error' : ''}`}>
                  <span>الاسم الكامل</span>
                  <input
                    type="text"
                    value={form.full_name}
                    onChange={(event) => handleFieldChange('full_name', event.target.value)}
                    disabled={isSubmitting}
                    autoComplete="name"
                  />
                  {fieldErrors.full_name && <small>{fieldErrors.full_name}</small>}
                </label>

                <label className={`user-edit-field ${fieldErrors.email ? 'has-error' : ''}`}>
                  <span>البريد الإلكتروني</span>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(event) => handleFieldChange('email', event.target.value)}
                    disabled={isSubmitting}
                    autoComplete="email"
                  />
                  {fieldErrors.email && <small>{fieldErrors.email}</small>}
                </label>

                <label className={`user-edit-field ${fieldErrors.phone ? 'has-error' : ''}`}>
                  <span>الهاتف</span>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(event) => handleFieldChange('phone', event.target.value)}
                    disabled={isSubmitting}
                    autoComplete="tel"
                  />
                  {fieldErrors.phone && <small>{fieldErrors.phone}</small>}
                </label>
              </div>
            </section>

            <section className="user-edit-section">
              <div className="user-edit-section-header">
                <h3>الصلاحيات والتحكم</h3>
                <p>عرض أو تعديل صلاحية الحساب وحالته بحسب صلاحياتك الحالية.</p>
              </div>

              <div className="user-edit-grid user-edit-grid-tight">
                {canManageRole ? (
                  <label className={`user-edit-field ${fieldErrors.role ? 'has-error' : ''}`}>
                    <span>الدور / الصلاحية</span>
                    <select
                      value={form.role}
                      onChange={(event) => handleFieldChange('role', event.target.value)}
                      disabled={isSubmitting}
                    >
                      {roleOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {fieldErrors.role && <small>{fieldErrors.role}</small>}
                  </label>
                ) : (
                  <div className="user-edit-readonly-card">
                    <span className="user-edit-readonly-label">الدور / الصلاحية</span>
                    <strong>{getRoleLabel(roleOptions, activeUser.role)}</strong>
                    <small>لا يمكنك تعديل الصلاحية لهذا الحساب من الجلسة الحالية.</small>
                  </div>
                )}

                {canManageStatus ? (
                  <label className={`user-edit-switch-card ${fieldErrors.is_active ? 'has-error' : ''}`}>
                    <div>
                      <span className="user-edit-readonly-label">حالة الحساب</span>
                      <strong>{form.is_active ? 'الحساب مفعّل' : 'الحساب معطّل'}</strong>
                      <small>يمكنك تفعيل الحساب أو إيقافه بدون مغادرة الصفحة.</small>
                    </div>

                    <span className="user-edit-switch">
                      <input
                        type="checkbox"
                        checked={form.is_active}
                        onChange={(event) => handleFieldChange('is_active', event.target.checked)}
                        disabled={isSubmitting}
                      />
                      <span className="user-edit-switch-track" />
                    </span>
                  </label>
                ) : (
                  <div className="user-edit-readonly-card">
                    <span className="user-edit-readonly-label">حالة الحساب</span>
                    <strong>{activeUser.is_active ? 'الحساب نشط' : 'الحساب غير نشط'}</strong>
                    <small>الحالة الحالية معروضة لك فقط بدون صلاحية تعديل.</small>
                  </div>
                )}
              </div>
            </section>

            <section className="user-edit-section">
              <div className="user-edit-section-header">
                <h3>الأمان</h3>
                <p>فعّل إعادة تعيين كلمة المرور فقط عند الحاجة، مع تحقق فوري قبل الحفظ.</p>
              </div>

              <label className="user-edit-switch-card is-security">
                <div>
                  <span className="user-edit-readonly-label">إعادة تعيين كلمة المرور</span>
                  <strong>تعيين كلمة مرور جديدة لهذا الحساب</strong>
                  <small>اترك هذا الخيار مغلقاً إذا لم تكن تريد تغيير كلمة المرور الحالية.</small>
                </div>

                <span className="user-edit-switch">
                  <input
                    type="checkbox"
                    checked={form.wantsPasswordReset}
                    onChange={(event) => handlePasswordToggle(event.target.checked)}
                    disabled={isSubmitting}
                  />
                  <span className="user-edit-switch-track" />
                </span>
              </label>

              {form.wantsPasswordReset && (
                <div className="user-edit-grid user-edit-grid-tight">
                  <label className={`user-edit-field ${fieldErrors.password ? 'has-error' : ''}`}>
                    <span>كلمة المرور الجديدة</span>
                    <div className="user-edit-field-with-icon">
                      <FaLock />
                      <input
                        type="password"
                        value={form.password}
                        onChange={(event) => handleFieldChange('password', event.target.value)}
                        disabled={isSubmitting}
                        autoComplete="new-password"
                      />
                    </div>
                    {fieldErrors.password && <small>{fieldErrors.password}</small>}
                  </label>

                  <label className={`user-edit-field ${fieldErrors.passwordConfirm ? 'has-error' : ''}`}>
                    <span>تأكيد كلمة المرور</span>
                    <div className="user-edit-field-with-icon">
                      <FaKey />
                      <input
                        type="password"
                        value={form.passwordConfirm}
                        onChange={(event) => handleFieldChange('passwordConfirm', event.target.value)}
                        disabled={isSubmitting}
                        autoComplete="new-password"
                      />
                    </div>
                    {fieldErrors.passwordConfirm && <small>{fieldErrors.passwordConfirm}</small>}
                  </label>
                </div>
              )}
            </section>
          </form>
        </div>

        <footer className="user-edit-dialog-footer">
          <div className="user-edit-footer-status">
            {hasChanges ? (
              <span className="is-pending">هناك تعديلات غير محفوظة</span>
            ) : (
              <span className="is-idle">لا توجد تغييرات جديدة</span>
            )}
          </div>

          <div className="user-edit-footer-actions">
            <button
              type="button"
              className="btn-action-cancel user-edit-footer-button"
              onClick={handleBackdropClick}
              disabled={isSubmitting}
            >
              <FaTimes />
              إلغاء
            </button>

            <button
              type="submit"
              form={formId}
              className="btn-action-save user-edit-footer-button"
              disabled={isSubmitting || !hasChanges || !isValid}
            >
              {isSubmitting ? <span className="user-edit-spinner" aria-hidden="true" /> : <FaSave />}
              {isSubmitting ? 'جاري حفظ التعديلات...' : 'حفظ التغييرات'}
            </button>
          </div>
        </footer>
      </section>
    </div>,
    document.body
  );
};

export default EditUserDialog;
